import { toast } from "sonner";

const LOGO_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

export const LOGO_UPLOAD_ACCEPTED_FILE_TYPES = [".png", ".jpg", ".jpeg"];

const LOGO_UPLOAD_ACCEPTED_EXTENSIONS = [".png", ".jpg", ".jpeg"];

export const LOGO_UPLOAD_HELPER_TEXT =
  "Upload a PNG (transparent) or JPG. SVGs are not supported. Recommended width: 500–1500 px. Max file size: 25 MB.";

const isAcceptedLogoExtension = (fileName: string) =>
  LOGO_UPLOAD_ACCEPTED_EXTENSIONS.some((ext) => fileName.endsWith(ext));

const isSvgFile = (file: File) =>
  file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");

const readImageDimensions = (
  file: File,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read image dimensions"));
    };

    img.src = objectUrl;
  });

/**
 * Validates a logo file for upload. Shows a toast and returns false when invalid.
 */
export const validateAndSelectLogoFile = async (
  file: File,
): Promise<boolean> => {
  if (file.size > LOGO_UPLOAD_MAX_BYTES) {
    toast.error("File is larger than 25 MB");
    return false;
  }

  const fileName = file.name.toLowerCase();

  if (isSvgFile(file)) {
    toast.error("SVG files are not supported. Use PNG or JPG.");
    return false;
  }

  if (!isAcceptedLogoExtension(fileName)) {
    toast.error("Only PNG and JPG files are supported.");
    return false;
  }

  try {
    await readImageDimensions(file);
    return true;
  } catch {
    toast.error("Unable to read image dimensions");
    return false;
  }
};
